import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import termsClasses from './AcceptTerms.module.css';
import { useGetAttachedCompanies } from 'Backend/users/getAttachedCompanies';
import { useAcceptTerms } from 'Backend/company/acceptMigrationTerms';
import { isDefined } from 'utils/typeguards/isDefined';
import { usePrism } from 'Components/UI_Library/Prism/usePrism';
import LoadingBar from 'Components/UI_Library/LoadingIndicators/LoadingBar';
import Button from 'Components/UI_Library/FormElements/Button';
import { Notification } from 'Components/UI_Library/Notification';
import Checkbox from 'Components/UI_Library/FormElements/Checkbox';

const AcceptTerms = () => {
    const { hasFeatureEnabled, isPrismLoading } = usePrism();

    const [loadedPrism, setLoadedPrism] = useState(false);
    const [accepted, setAccepted] = useState(true);

    const [message, setMessage] = useState<{
        isError: boolean;
        msg: string;
    }>({ isError: false, msg: '' });

    const { data: companyResults, isFetching: getCompaniesLoading } = useGetAttachedCompanies();

    const { mutateAsync: acceptTerms, error: errorAcceptingTerms, isLoading, isSuccess, data } = useAcceptTerms();

    const history = useNavigate();

    const owner = companyResults?.data?.filter((company) => company.type === 'Owner');

    const ownerHasNotAccepted = owner?.filter((company) => !company.acceptedMigrationTerms);

    const companyIds = ownerHasNotAccepted?.map((company) => company.companyId).filter(isDefined);

    const noCompanies = !companyIds || !companyIds.length;

    const formatCompanyText = () => {
        const numOwners = companyIds?.length;
        if (!numOwners) return '';
        if (numOwners <= 1) return '1 company profile';
        return `${numOwners} company profiles`;
    };

    useEffect(() => {
        // Check if OFX Migration Prism flow is turned on for one of the user's company

        if (!getCompaniesLoading && !isPrismLoading && !loadedPrism) {
            if (
                !ownerHasNotAccepted?.length ||
                !owner
                    ?.map((co) => co.companyId)
                    .filter(isDefined)
                    .some((co) => hasFeatureEnabled('OFXMigration', co))
            ) {
                history('/switch');
            } else {
                setLoadedPrism(true);
            }
        }
    }, [
        companyResults,
        isPrismLoading,
        loadedPrism,
        getCompaniesLoading,
        history,
        hasFeatureEnabled,
        ownerHasNotAccepted,
        owner,
    ]);

    const acceptTermsOnSubmit = async () => {
        if (noCompanies) return;
        await acceptTerms(companyIds);
    };

    useEffect(() => {
        // Company successfully accepted terms
        if (isSuccess && data) history('/switch');

        if (errorAcceptingTerms) {
            setMessage({
                isError: true,
                msg: errorAcceptingTerms.title
                    ? `${errorAcceptingTerms.title} ${errorAcceptingTerms.detail}`
                    : 'An error occurred while accepting terms.',
            });
        }
    }, [errorAcceptingTerms, isSuccess, data, history]);

    if (!loadedPrism)
        return (
            <div className={termsClasses.loading}>
                <LoadingBar />
            </div>
        );

    return (
        <div className={termsClasses.newTerms}>
            <div className={termsClasses.top}>
                <div className={termsClasses.title}>
                    <h2>New terms and conditions</h2>
                </div>
                <p>
                    Paytron is now OFX and your account has been transitioned to OFX. The following OFX legal documents
                    govern your continued use of the Services and replace all existing terms you had with Paytron.
                </p>
                <ul>
                    <li>
                        <a
                            href="https://www.ofx.com/en-au/legal/general-terms-and-conditions/"
                            target="_blank"
                            rel="noreferrer"
                        >
                            OFX Master Terms and Conditions
                        </a>
                    </li>
                    <li>
                        <a href="https://www.ofx.com/en-au/legal/card-agreement/" target="_blank" rel="noreferrer">
                            OFX Card Supplement
                        </a>
                    </li>
                    <li>
                        <a
                            href="https://www.ofx.com/en-au/legal/subscription-agreement/"
                            target="_blank"
                            rel="noreferrer"
                        >
                            OFX Subscription Agreement
                        </a>
                    </li>
                    <li>
                        <a href="https://www.ofx.com/en-au/legal/privacy-policy" target="_blank" rel="noreferrer">
                            OFX Privacy Policy
                        </a>
                    </li>
                    <li>
                        <a
                            href="https://www.ofx.com/en-au/legal/target-market-determinations"
                            target="_blank"
                            rel="noreferrer"
                        >
                            OFX Target Market Determinations
                        </a>
                    </li>
                    <li>
                        <a
                            href="https://www.ofx.com/en-au/legal/product-disclosure-statement/"
                            target="_blank"
                            rel="noreferrer"
                        >
                            OFX Product Disclosure Statement
                        </a>
                    </li>
                    <li>
                        <a
                            href="https://www.ofx.com/en-au/legal/financial-services-guide/"
                            target="_blank"
                            rel="noreferrer"
                        >
                            OFX Financial Services Guide
                        </a>
                    </li>
                </ul>
                <p>
                    You are the designated &rdquo;Owner&rdquo; of the {formatCompanyText()} listed below that have not
                    yet accepted the terms and you are acting as the Authorised Signatory for each company. The above
                    legal documents apply separately to each company in its own right and bind each company separately
                    as a separate legal entity.
                </p>
                <p className={termsClasses.companies}>
                    {ownerHasNotAccepted?.map(
                        (company, index) =>
                            ` ${company.companyName}${
                                ownerHasNotAccepted?.length && index < ownerHasNotAccepted.length - 1 ? ',' : ''
                            }`
                    )}
                </p>
                <p>
                    Please review these important documents and accept them by indicating below. If you no longer need
                    our services, please email support@paytron.com about closing your account.
                </p>
            </div>
            <div className={termsClasses.bottom}>
                <div className={termsClasses.divider} />
                <p>
                    By clicking &quot;Accept and continue&quot;, I confirm that I am authorised to bind each of the
                    above businesses in its own distinct capacity and have read and agree to all the legal documents on
                    behalf of each of these businesses.
                </p>
                <div className={termsClasses.checkbox}>
                    <Checkbox
                        row
                        text="I consent to the OFX Group of companies using my personal information for the purposes set out in the OFX Privacy Policy, including for identity verification purposes."
                        checked={accepted}
                        blue
                        toggle={() => setAccepted(!accepted)}
                        style={{ marginBottom: '20px', width: '100%' }}
                    />
                </div>
                <Notification
                    isError={message.isError}
                    message={message.msg}
                    style={{ padding: '0 0 0 10px' }}
                    handleCloseMsg={() => setMessage({ isError: false, msg: '' })}
                />
                <div className={termsClasses.termsButtons}>
                    <a target="_blank" rel="noreferrer" href="https://www.ofx.com/en-au/about-us/paytron/#FAQs">
                        <Button type="white-condensed" text="Learn more" />
                    </a>
                    <Button
                        type="primary"
                        text="Accept and continue"
                        loading={isLoading}
                        disabled={!accepted || isLoading}
                        onClick={acceptTermsOnSubmit}
                    />
                </div>
            </div>
        </div>
    );
};

export default AcceptTerms;
